import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Bot, User, Square, SquarePen, Info, BookOpen, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArcheLayout } from '../../components/ArcheLayout';
import { FavoriteButton } from '../../components/FavoriteButton';
import { useSidebar } from '../../context/SidebarContext';
import { authApi } from '@/lib/auth-api';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatMessageContent } from '@/lib/format-error';
import { InsufficientCreditsMessage } from '@/credits/components';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import { useTextHighlightLearnMore } from '@/lib/hooks/useTextHighlightLearnMore';
import { LearnMorePopup } from '@/components/LearnMorePopup';

// WebSocket endpoint from environment
const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';

// Maximum number of messages to send in conversation history
// Keeps payload size manageable and reduces token costs
// 20 messages = ~10 back-and-forth exchanges
const MAX_CONVERSATION_MESSAGES = 20;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

function LearnAboutFiasChatContent() {
  const { isCollapsed, isMobile, toggle } = useSidebar();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Smart auto-scroll: only scrolls to bottom if user is already near bottom
  const { containerRef, anchorRef, scrollToBottomIfNeeded, scrollToBottom } = useAutoScroll();

  // WebSocket streaming state
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  // WebSocket streaming hook
  const {
    streamingText,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken: authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
  });

  // Combine all node streaming text into one string
  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Handler to submit a query directly (used by learn more feature)
  const submitQueryDirectly = useCallback(
    async (query: string) => {
      if (!query.trim() || isStreaming) return;

      // Generate unique ID for this message pair
      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      // Add user message
      const userMessage: Message = {
        id: userMessageId,
        role: 'user',
        content: query.trim(),
      };

      // Add placeholder for assistant response
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setInputValue('');
      setIsStreaming(true);

      // Scroll to bottom so user can see the new message
      setTimeout(() => scrollToBottom(), 50);

      // Build conversation history
      const allMessages = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      await streamWithWebSocket(assistantMessageId, conversationHistory);
    },
    [isStreaming, messages, scrollToBottom],
  );

  // Text highlight "Learn More" feature
  const highlight = useTextHighlightLearnMore(
    messagesContainerRef,
    submitQueryDirectly,
    isStreaming,
  );

  // Update assistant message with streaming text
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Handle streaming completion
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingComplete, currentAssistantMessageId, resetStreaming]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      // Check if it's an authorization error
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED' ||
        streamingError.message.toLowerCase().includes('token');

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  // Reset conversation
  const handleReset = useCallback(() => {
    setMessages([]);
    setInputValue('');
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [resetStreaming, isMobile]);

  // Auto-scroll to bottom when messages change (only if user is already at bottom)
  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  // Focus textarea when streaming completes (skip on mobile)
  useEffect(() => {
    if (!isStreaming && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isStreaming, isMobile]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // WebSocket streaming
  const streamWithWebSocket = async (
    assistantMessageId: string,
    conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    try {
      // Start the workflow and get requestId
      const result = await api.startArche('arche_learn_fias_chat', {
        messages: conversationHistory,
        provider: 'openai',
      });

      // Set up WebSocket streaming
      setCurrentRequestId(result.requestId);
      setCurrentAssistantMessageId(assistantMessageId);
      // The useStreaming hook will handle the rest via effects
    } catch (err) {
      console.error('WebSocket streaming failed:', err);

      // Check if it's an authorization error
      const errorStr = String(err).toLowerCase();
      const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : 'Sorry, something went wrong. Please try again.';

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isStreaming) return;

    // Generate unique ID for this message pair
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;

    // Add user message
    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: trimmedInput,
    };

    // Add placeholder for assistant response
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputValue('');
    setIsStreaming(true);

    // Build conversation history - always include context for better responses
    // Truncate to last N messages to keep payload size manageable and reduce token costs
    const allMessages = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);
    const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
    const conversationHistory = truncatedMessages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    await streamWithWebSocket(assistantMessageId, conversationHistory);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStop = useCallback(() => {
    // Reset WebSocket streaming
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();

    setIsStreaming(false);
    // Mark any streaming message as complete
    setMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
    );
  }, [resetStreaming]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <div className="flex items-center gap-2 min-w-0 sm:flex-1">
            {/* Sidebar toggle (always rendered to maintain layout, hidden when open) */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <BookOpen className="h-5 w-5 text-primary flex-shrink-0" />
            <h1 className="text-lg font-semibold truncate">Learn About Fias</h1>
            <Link
              to="/marketplace/arches/arche_learn_fias_chat"
              className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
              title="View arche details"
            >
              <Info className="h-5 w-5" />
            </Link>
            <FavoriteButton archeId="arche_learn_fias_chat" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link to="/a/arche_learn_about_fias">
                <BookOpen className="h-4 w-4" />
                Resources
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isStreaming || messages.length === 0}
              className="gap-1.5 [&_svg]:size-5!"
            >
              <SquarePen />
              New Chat
            </Button>
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <img
                src="/fias-logo-image.png"
                alt="Fias AI"
                className="h-16 w-16 mb-4 dark:invert dark:brightness-90"
              />
              <h2 className="text-xl font-medium text-foreground mb-2">Ask About Fias</h2>
              <p className="text-muted-foreground max-w-md">
                Ask questions about Fias, our platform, and how AI monetization works.
                <br />
                <br />
                Answers are powered by our knowledge base including the whitepaper, executive
                summary, and overview materials.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-3 max-w-md">
                AI responses may contain errors. Do not rely on any answers for financial advice.
                Always verify important information from official sources.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-lg">
                {[
                  'What is Fias?',
                  'How do I use Fias?',
                  'What are Arches?',
                  'What are Entities?',
                  'How do I create my own Arche?',
                ].map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    onClick={() => submitQueryDirectly(suggestion)}
                    className="text-sm"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div ref={messagesContainerRef} className="space-y-6">
              {/* Learn More popup for text selection */}
              {highlight.isVisible && highlight.position && (
                <LearnMorePopup position={highlight.position} onClick={highlight.handleLearnMore} />
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-4',
                    message.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-3',
                      message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted',
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="text-xs text-muted-foreground mb-1">Fias Knowledge Base</div>
                    )}
                    {/* Main response content */}
                    <div className="break-words">
                      {message.isStreaming && !message.content ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <div className="flex gap-0.5">
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                              style={{ animationDelay: '0ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                              style={{ animationDelay: '150ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                              style={{ animationDelay: '300ms' }}
                            />
                          </div>
                          <span className="text-sm">Searching knowledge base...</span>
                        </div>
                      ) : message.role === 'assistant' ? (
                        (() => {
                          const {
                            content: displayContent,
                            isError,
                            isInsufficientCredits,
                          } = formatMessageContent(message.content);
                          return (
                            <div
                              className={cn(
                                'prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:my-3 prose-strong:text-foreground',
                                isError && 'text-amber-600 dark:text-amber-400',
                              )}
                            >
                              {isError ? (
                                isInsufficientCredits ? (
                                  <InsufficientCreditsMessage />
                                ) : (
                                  displayContent
                                )
                              ) : (
                                <ReactMarkdown>{displayContent}</ReactMarkdown>
                              )}
                              {message.isStreaming && message.content && (
                                <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          {message.content}
                          {message.isStreaming && message.content && (
                            <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={anchorRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <form onSubmit={handleSubmit} className="relative">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about Fias..."
              disabled={isStreaming}
              className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
              rows={1}
              style={{ height: 'auto' }}
            />
            {isStreaming ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={handleStop}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!inputValue.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </form>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}

// Outer component that wraps with ArcheLayout
export function LearnAboutFiasChatPage() {
  return (
    <ArcheLayout>
      <LearnAboutFiasChatContent />
    </ArcheLayout>
  );
}

export default LearnAboutFiasChatPage;
